<template>
    <div class="trendChart">
        <div :id="chartId" class="trend-chart" ref="trendChart"></div>
    </div>
</template>

<script>
import * as echarts from 'echarts';

export default {
    props: {
        values: Object, // 图表数据（{日期: 值}）
        type: String, // 唯一标识（区分不同图表）
        title: String, // 图表标题

        isgrid: {
            type: Boolean,
            default: false,
        },
    },
    data () {
        return {
            trendChart: null, // ECharts实例
            seriesArr: [],
            // 结合type生成唯一ID，避免重复
            chartId: `trend-chart-${this.type}-${Math.random().toString(36).slice(2, 10)}`,
            option: {
                backgroundColor: 'transparent',
                tooltip: {
                    extraCssText: 'borderRadius:6px;',
                    formatter (params) {
                        const item = params[0];
                        return `<div><div> ${item.axisValue} </div><div> ${item.seriesName}：${item.data}</div></div>`;
                    },
                    show: true,
                    trigger: 'axis',
                    axisPointer: {
                        type: 'line',
                        crossStyle: { color: '#DBF0FF' },
                        lineStyle: { color: { color: '#86909C', type: 'dashed' } },
                    },
                },
                xAxis: {
                    type: 'category',
                    triggerEvent: true,
                    boundaryGap: false,
                    data: [],
                    splitLine: {},
                    axisLine: { show: true },
                    axisTick: { show: false },
                    axisLabel: {
                        color: '#ffffff80',
                        fontSize: 12,
                        // interval: function (index, value) {
                        //     const dataLength = Object.keys(this.values || {}).length;
                        //     return index % 2 !== 0;
                        // }.bind(this),
                    },
                },

                grid: [
                    {
                        top: '4%',
                        right: '3%',
                        left: '0.5%',
                        bottom: '0%',
                        containLabel: true,
                    },
                ],
                yAxis: {
                    nameLocation: 'start',
                    nameTextStyle: { color: '#597faa', fontSize: 12 },
                    type: 'value',
                    splitLine: {
                        show: true,
                        lineStyle: { width: 1, type: 'dashed', color: '#666' },
                    },
                    axisLine: { show: true },
                    axisTick: { show: false },
                    axisLabel: { color: '#ffffff80', fontSize: 12 },
                },
                series: [],
                emphasis: { disabled: true },
            },
            lineStyle: {
                type: 'line',
                smooth: true,
                symbol: 'circle',
                showSymbol: true,
                symbolSize: 6,
                lineStyle: {
                    width: 2,
                    color: '#2E7DF8',
                    shadowBlur: 0,
                    shadowColor: 'transparent',
                    shadowOffsetX: 0,
                    shadowOffsetY: 0,
                    opacity: 1,
                    type: 'solid',
                },
                areaStyle: {
                    color: 'rgba(219, 240, 255, 0)',
                    shadowBlur: 0,
                    shadowColor: 'transparent',
                },
                itemStyle: {
                    color: '#071150',
                    borderColor: '#4080FF',
                    borderWidth: 2,
                },
                data: [],
            },
        };
    },
    watch: {
        // 监听数据变化，强制重绘（增加错误捕获）
        values: {
            handler (newVal) {
                try {
                    console.log(`图表[${this.type}]数据更新:`, newVal);
                    this.getChart();
                } catch (error) {
                    console.error(`图表[${this.type}]数据更新失败:`, error);
                }
            },
            immediate: true, // 初始化时立即执行
            deep: true, // 深度监听对象变化
        },
    },
    mounted () {
        // 绑定resize事件（使用箭头函数确保this指向正确）
        window.addEventListener('resize', () => this.handleResize(), true);
        // 确保DOM渲染完成后初始化图表
        this.$nextTick(() => {
            this.getChart();
        });
    },
    methods: {
        // 处理窗口resize（增加安全检查）
        handleResize () {
            if (this.trendChart && this.$refs.trendChart && document.contains(this.$refs.trendChart)) {
                this.trendChart.resize();
            }
        },

        // 绘制图表（核心修复：完善DOM和实例检查）
        getChart () {
            const dom = document.getElementById(this.chartId);
            if (!dom || !document.contains(dom)) {
                console.warn(`图表[${this.type}]DOM不存在，跳过渲染`);
                return;
            }

            if (this.trendChart) {
                try {
                    if (typeof this.trendChart.dispose === 'function') {
                        this.trendChart.dispose();
                    }
                } catch (error) {
                    console.warn(`图表[${this.type}]销毁旧实例失败:`, error);
                } finally {
                    this.trendChart = null;
                }
            }
            const parentDom = this.$refs.trendChart?.parentElement;
            if (!parentDom) {
                console.warn(`图表[${this.type}]父容器不存在`);
                return;
            }

            const { clientWidth, clientHeight } = parentDom;
            dom.style.width = `${clientWidth}px`;
            dom.style.height = `${clientHeight}px`;

            // 5. 初始化新实例（增加错误捕获）
            try {
                this.trendChart = echarts.init(dom);
            } catch (error) {
                console.error(`图表[${this.type}]初始化失败:`, error);
                return;
            }

            // 6. 处理数据并设置配置
            if (this.values && Object.keys(this.values).length) {
                const keys = Object.keys(this.values); // x轴数据（日期）
                const values = Object.values(this.values); // y轴数据（值）

                const style = {
                    ...this.lineStyle,
                    name: this.title || '数据趋势',
                    data: values,
                };
                this.seriesArr = [style];
                this.option.xAxis.data = keys;
                this.option.series = this.seriesArr;
            } else {
                // 无数据时清空图表
                this.option.xAxis.data = [];
                this.option.series = [];
            }

            // 7. 应用配置（增加错误捕获）
            try {
                this.trendChart.setOption(this.option, { notMerge: true });
            } catch (error) {
                console.error(`图表[${this.type}]设置配置失败:`, error);
            }
        },
    },
    beforeDestroy () {
        // 组件销毁时清理资源
        window.removeEventListener('resize', () => this.handleResize(), true);
        if (this.trendChart) {
            try {
                if (typeof this.trendChart.dispose === 'function') {
                    this.trendChart.dispose();
                }
            } catch (error) {
                console.warn(`图表[${this.type}]销毁失败:`, error);
            } finally {
                this.trendChart = null;
            }
        }
    },
};
</script>

<style lang="scss" scoped>
.trendChart {
    position: relative;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    overflow: hidden;
}

.trend-chart {
    width: 100% !important;
    height: 100% !important;
    box-sizing: border-box !important;
    box-shadow: none !important;
    filter: drop-shadow(0 0 0px transparent) !important;
    -webkit-filter: drop-shadow(0 0 0px transparent) !important;
}
</style>
